import React, { useState, useMemo, useCallback, useEffect } from "react"
import ButtonTooltip from "components/Common/ButtonTooltip"
import StatusBadge from "components/Common/StatusBadge"
import { Modal, ModalHeader, ModalBody, Button } from "reactstrap"
import { formatReadableDate } from "utils/formatDate"
import VehicleStockContentAddEditModal from "./VehicleStockContentAddEditModal"
import { contentData } from "helpers/vehicle_stock_publish_helpers"
import TableLocal from "components/Common/TableLocal/TableLocal"
import TableLocalPagination from "components/Common/TableLocal/TableLocalPagination"

const VehicleContentsModal = ({
  modal,
  toggle,
  selectedData,
  params,
  updateParams,
}) => {
  const [currentPage, setCurrentPage] = useState(1)
  const [itemsPerPage, setItemsPerPage] = useState(5)
  const [sortOrder, setSortOrder] = useState("desc")

  // Initialize current page from URL params
  useEffect(() => {
    if (params?.p_page) {
      setCurrentPage(Number(params.p_page))
    } else {
      setCurrentPage(1)
    }
  }, [params?.p_page, modal])

  // Reset to page 1 when modal opens with new data
  useEffect(() => {
    if (modal && selectedData) {
      setCurrentPage(1)
      // Ensure URL param is also set to 1 when modal opens
      if (params?.p_page !== 1) {
        updateParams(prev => ({
          ...prev,
          p_page: 1,
        }))
      }
    }
  }, [modal, selectedData])

  // Sort contents by date
  const sortedContents = useMemo(() => {
    if (!selectedData?.contents) return []
  const contents = Array.isArray(selectedData?.contents) ? selectedData.contents : [];

    return [...contents].sort((a, b) => {
      const dateA = new Date(a.date)
      const dateB = new Date(b.date)

      if (sortOrder === "desc") {
        return dateB - dateA
      } else {
        return dateA - dateB
      }
    })
  }, [selectedData?.contents, sortOrder])

  const toggleSortOrder = useCallback(() => {
    setSortOrder(prev => (prev === "desc" ? "asc" : "desc"))
    setCurrentPage(1)
    updateParams(prev => ({
      ...prev,
      p_page: 1,
    }))
  }, [updateParams])

  const handlePageChange = useCallback(
    page => {
      setCurrentPage(page)
      updateParams(prev => ({
        ...prev,
        p_page: page,
      }))
    },
    [updateParams]
  )

  const handleItemsPerPageChange = useCallback(
    newItemsPerPage => {
      setItemsPerPage(newItemsPerPage)
      setCurrentPage(1)
      updateParams(prev => ({
        ...prev,
        p_page: 1,
      }))
    },
    [updateParams]
  )

  // Static columns that don't depend on props or state
  const staticColumns = useMemo(
    () => [
      {
        header: "ID",
        accessor: "id",
      },
      {
        header: "Content Link",
        cell: row =>
          row?.url ? (
            <ButtonTooltip
              type="view"
              onClick={() => window.open(row.url, "_blank")}
              btnId={`${row.id}-content-view-link`}
              btnName="View Content Link"
            />
          ) : (
            "-"
          ),
      },
      {
        header: "Remarks",
        accessor: "remarks",
      },
      {
        header: "Published",
        cell: row => (
          <StatusBadge
            value={row.is_published}
            trueLabel="Yes"
            falseLabel="No"
          />
        ),
      },
      {
        header: "Action",
        cell: row => (
          <VehicleStockContentAddEditModal
            currentContent={row}
            isLabel={false}
            data={selectedData}
          />
        ),
      },
    ],
    [selectedData]
  ) // Only depends on selectedData

  // Dynamic date column that depends on sortOrder
  const dateColumn = useMemo(
    () => ({
      header: (
        <div
          className="d-flex align-items-center gap-1 cursor-pointer"
          onClick={toggleSortOrder}
          style={{ cursor: "pointer" }}
        >
          <span>Date</span>
          <i
            className={`mdi mdi-arrow-${sortOrder === "desc" ? "down" : "up"}`}
          />
        </div>
      ),
      cell: row => <span>{formatReadableDate(row.date)}</span>,
    }),
    [toggleSortOrder, sortOrder]
  )

  // Combine columns
  const columns = useMemo(
    () => [...staticColumns, dateColumn],
    [staticColumns, dateColumn]
  )

  if (!selectedData) return null

  return (
    <Modal isOpen={modal} toggle={toggle} size="lg" centered>
      <ModalHeader toggle={toggle}>
        Vehicle Stock ID:{" "}
        {selectedData.stock?.stock_id ?? selectedData.vehicle_stock_id}
      </ModalHeader>
      <ModalBody>
        <div className="d-flex flex-wrap justify-content-between align-items-center mb-3">
          <div>
            <strong>Platform:</strong> {selectedData.platform?.name ?? "-"}
            <span className="ms-3 text-muted">
              <i
                className={`mdi mdi-sort-${
                  sortOrder === "desc" ? "descending" : "ascending"
                } me-1`}
              />
              Sorted by date (
              {sortOrder === "desc" ? "newest first" : "oldest first"})
            </span>
          </div>

          <div className="d-flex align-items-center gap-2">
            <Button
              color="outline-secondary"
              size="sm"
              onClick={toggleSortOrder}
              className="d-flex align-items-center"
            >
              <i
                className={`mdi mdi-sort-${
                  sortOrder === "desc" ? "descending" : "ascending"
                } me-1`}
              />
              {sortOrder === "desc" ? "Newest First" : "Oldest First"}
            </Button>

            <VehicleStockContentAddEditModal
              currentContent={contentData}
              buttonLabel="Add Content"
              data={selectedData}
            />
          </div>
        </div>

        <TableLocal
          columns={columns}
          data={sortedContents}
          itemsPerPage={itemsPerPage}
          currentPage={currentPage}
          tableClass="align-middle"
          emptyMessage="No content available"
        />

        <TableLocalPagination
          data={sortedContents}
          itemsPerPage={itemsPerPage}
          currentPage={currentPage}
          onPageChange={handlePageChange}
          onItemsPerPageChange={handleItemsPerPageChange}
          showItemsPerPage={true}
          itemsPerPageOptions={[5, 10, 20]}
        />

        <div className="text-end mt-3">
          <Button size="sm" color="danger" onClick={toggle}>
            Close
          </Button>
        </div>
      </ModalBody>
    </Modal>
  )
}

VehicleContentsModal.displayName = "VehicleContentsModal"

export default React.memo(VehicleContentsModal)
